import {
  createMessage,
  extractErrorMessage,
} from 'works/legacy/lib/message-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { FormInstance } from 'antd';
import { FullValidationErrorFragmentType } from '@common/graphql/fragments';
import { ApolloFetchResultType } from '@common/hooks';

const isFormError = (
  error: unknown
): error is {
  kind: 'formError';
  errors: FullValidationErrorFragmentType[];
} => {
  if (typeof error === 'object' && error) {
    return (error as Record<string, string>).kind === 'formError';
  }

  return false;
};

export const catchFormErrorsWith =
  (form: FormInstance, defaultMessage: string) => (error: unknown) => {
    if (isFormError(error)) {
      console.error('Response error:', error.errors);

      error.errors.forEach((error) => {
        if (error.attribute === 'base') {
          createMessage('error', defaultMessage);
        }
      });

      addErrorsToForm(form, error.errors);
    } else {
      catchErrorWith(defaultMessage)(error);
    }
  };

export const catchErrorWith = (defaultMessage: string) => (error: unknown) => {
  console.error('Response error:', error);
  createMessage('error', typeof error === 'string' ? error : defaultMessage);
};

export const rejectSimpleErrors =
  <K extends string, T extends Record<K, { error?: string }>>(on: keyof T) =>
  (response: ApolloFetchResultType<T>): Promise<T> => {
    const [hadErrors, errorMessage] = extractErrorMessage(response);
    const handledError = response.data?.[on]?.error;

    if (hadErrors || handledError) {
      return Promise.reject(errorMessage || handledError);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return Promise.resolve(response.data!);
  };

export const rejectValidationErrors =
  <
    K extends string,
    T extends Record<K, { errors: FullValidationErrorFragmentType[] | null }>,
  >(
    on: keyof T
  ) =>
  (response: ApolloFetchResultType<T>): Promise<T> => {
    const [hadErrors, errorMessage] = extractErrorMessage(response);

    if (hadErrors) {
      return Promise.reject(errorMessage);
    }

    const validationErrors = response.data?.[on]?.errors;
    if (validationErrors) {
      return Promise.reject({ kind: 'formError', errors: validationErrors });
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return Promise.resolve(response.data!);
  };
